import React, { useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
// Api
import {
  GET_TICKET_EVENT_HOST,
  GET_TICKET_EVENT_HOSTS,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEventHosts,
  GetTicketEventHostsVariables,
} from 'api/ticketing/types/GetTicketEventHosts';
import {
  GetTicketEventHost,
  GetTicketEventHostVariables,
} from 'api/ticketing/types/GetTicketEventHost';
import { UserRole } from 'api/graphql-global-types';
// Helpers
import { getHostLabel } from './helpers';
// Components
import SelectInfiniteScroll, {
  LoadSelectInfiniteScrollOptionsArgs,
  LoadSelectInfiniteScrollOptionsResult,
} from 'components/common/SelectInfiniteScroll/SelectInfiniteScroll';

export const TICKET_HOST_STORE_ROLES = [
  UserRole.Organization,
  UserRole.Athlete,
  UserRole.ContentCreator,
];

type HostSelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
};

const HostSelect = ({
  value,
  onChange,
  disabled,
  placeholder = 'Search organizations, athletes and creators',
  'aria-label': ariaLabel = 'Host',
}: HostSelectProps): JSX.Element => {
  const client = useApolloClient();

  // The page of results may not include the host already chosen
  const { data: selectedHost } = useQuery<
    GetTicketEventHost,
    GetTicketEventHostVariables
  >(GET_TICKET_EVENT_HOST, {
    variables: { input: { id: value } },
    skip: !value,
  });

  const loadOptions = useCallback(
    async ({
      search,
      offset,
      limit,
    }: LoadSelectInfiniteScrollOptionsArgs): Promise<LoadSelectInfiniteScrollOptionsResult> => {
      const { data } = await client.query<
        GetTicketEventHosts,
        GetTicketEventHostsVariables
      >({
        query: GET_TICKET_EVENT_HOSTS,
        variables: {
          storeRoles: TICKET_HOST_STORE_ROLES,
          input: { searchTerm: search || null, offset, limit },
        },
        fetchPolicy: 'network-only',
      });

      return {
        options: (data?.adminStores.entities || []).map((store) => ({
          value: store.id,
          label: getHostLabel(store),
        })),
        total: data?.adminStores.total || 0,
      };
    },
    [client]
  );

  const host = selectedHost?.store;

  return (
    <SelectInfiniteScroll
      value={value}
      onChange={(next) => onChange?.(next)}
      loadOptions={loadOptions}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      extraOptions={
        host
          ? [
              {
                value: host.id,
                label: getHostLabel(host),
              },
            ]
          : []
      }
    />
  );
};

export default HostSelect;
